import { useEffect, useReducer } from "react";
import type { ReactNode } from "react";

import { cachedFile, fileFailed, loadFile } from "@/lib/api/fileCache";

export interface AuthenticatedImageProps {
  fileId: string | null | undefined;
  alt: string;
  className?: string;
  fallback?: ReactNode;
}

/**
 * An image behind the authenticated file endpoint.
 *
 * These are passport and ID scans, so the endpoint requires a bearer token and
 * a plain `<img src>` cannot send one. The bytes are fetched and handed to the
 * tag as an object URL, which also keeps the token out of the URL bar and out
 * of any referrer header.
 *
 * The cache is the state. Holding a copy in the component meant resetting it
 * whenever the file changed, which is a render's worth of the wrong picture;
 * reading through on every render also means an already-loaded face paints on
 * the first frame instead of flashing its fallback for a tick.
 */
export function AuthenticatedImage({
  fileId,
  alt,
  className,
  fallback = null,
}: AuthenticatedImageProps) {
  const [, rerender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    if (!fileId || cachedFile(fileId) || fileFailed(fileId)) return;

    let cancelled = false;
    void loadFile(fileId)
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) rerender();
      });

    return () => {
      cancelled = true;
    };
  }, [fileId]);

  const url = fileId ? cachedFile(fileId) : null;
  if (!url) return <>{fallback}</>;
  return <img src={url} alt={alt} className={className} />;
}
